/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { switchMap, map, catchError, of, filter, mergeMap, concatMap, merge, tap } from "rxjs";
import { Epic } from "redux-observable";

import { Action, ActionType } from "lib/ActionType";

import ServicesContext from "services/ServicesContext";

import * as slice from "./selectedAssetFilters.slice";
import * as assetGroupSlice from "./assetGroups.slice";
import { CompanyAssetGroupModel } from "data/models/CompanyModel";


type ActionsType = ActionType<typeof slice.actions>;
type TEpic<I extends Action<any> = any, O extends I & ActionsType = any> = Epic<I, O, { selectedAssetFilters: slice.SelectedAssetFiltersState }, ServicesContext>;


const saveSelectedAssetFilters: TEpic<ActionType<typeof slice.actions.saveSelectedAssetFilters>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.saveSelectedAssetFilters.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.copyAssetGroupFilters(
            action.payload.assetGroup.companyId,
            "_",
            action.payload.assetGroup.id,
            true
         ).pipe(
            map(() => slice.actions.selectedAssetFilterSaved()),
            catchError(err => of(slice.actions.selectedAssetFilterSaveError({ err })))
         )
      )
   )

}


const saveSelectedAssetFiltersAs: TEpic<ActionType<typeof slice.actions.saveSelectedAssetFiltersAs>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.saveSelectedAssetFiltersAs.type
      ),
      switchMap(
         action => typeof action.payload.assetGroupOrName === "string" ?

            deps.api.companyDataProvider.createAssetGroup(
               action.payload.companyId,
               action.payload.assetGroupOrName,
               action.payload.assetGroupDescription
            ).pipe(
               switchMap(

                  assetGroup => deps.api.companyDataProvider.copyAssetGroupFilters(
                     assetGroup.companyId,
                     "_",
                     assetGroup.id,
                     true
                  ).pipe(
                     switchMap(
                        () => of(
                           assetGroupSlice.actions.assetGroupCreated({ assetGroup }),
                           assetGroupSlice.actions.selectAssetGroupById({ id: assetGroup.id }),
                           slice.actions.selectedAssetFilterSaved()
                        )
                     ),
                     catchError(err => of(slice.actions.selectedAssetFilterSaveError({ err })))
                  )

               ),
               catchError(err => of(slice.actions.selectedAssetFilterSaveError({ err })))
            )

         :
            deps.api.companyDataProvider.updateAssetGroup({
               ...action.payload.assetGroupOrName,
               description: action.payload.assetGroupDescription,
            }).pipe(
               switchMap(
                  assetGroup => deps.api.companyDataProvider.copyAssetGroupFilters(
                     action.payload.companyId,
                     "_",
                     assetGroup.id,
                     !action.payload.merge
                  ).pipe(
                     switchMap(
                        () => of (
                           assetGroupSlice.actions.assetGroupUpdated({ assetGroup }),
                           assetGroupSlice.actions.selectAssetGroupById({ id: (action.payload.assetGroupOrName as CompanyAssetGroupModel).id }),
                           slice.actions.selectedAssetFilterSaved()
                        )
                     ),
                     catchError(err => of(slice.actions.selectedAssetFilterSaveError({ err })))
                  )
               ),
               catchError(err => of (slice.actions.selectedAssetFilterSaveError({ err }))),

            )


      )
   )

}



const loadFilters: TEpic<ActionType<typeof slice.actions.loadAssetFilters>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadAssetFilters.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.listAssetGroupsFilters(
            action.payload.companyId, "_"
         ).pipe(
            map(filters => slice.actions.assetFiltersLoaded({ filters })),
            catchError(err => of(slice.actions.filtersLoadingError({ err })))
         )
      )
   )

}

const filtersLoaded: TEpic<ActionType<typeof slice.actions.assetFiltersLoaded>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.assetFiltersLoaded.type
      ),
      mergeMap(
         action => action.payload.filters.map(
            filter => slice.actions.loadAssetFilterDescs({ filter }))
         )
   )

}


const loadFilter: TEpic<ActionType<typeof slice.actions.loadAssetFilter>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadAssetFilter.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.readAssetGroupFilter(
            action.payload.companyId,
            "_", //action.payload.assetGroupId,
            action.payload.filterId
         ).pipe(
            map(filter => slice.actions.assetFilterLoaded({ filter })),
            catchError(err => of(slice.actions.assetFilterLoadingError({ err })))
         )
      )
   )

}


const filterLoaded: TEpic<ActionType<typeof slice.actions.assetFilterLoaded>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.assetFilterLoaded.type
      ),
      map(action => slice.actions.loadAssetFilterDescs({ filter: action.payload.filter }))
   )

}


const loadFilterDescs: TEpic<ActionType<typeof slice.actions.loadAssetFilterDescs>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadAssetFilterDescs.type
      ),
      mergeMap(
         action => {
            return deps.api.companyDataProvider.listAssetGroupsFilterDescs(
            action.payload.filter.companyId,
            "_", // action.payload.filter.filterGroupId,
            action.payload.filter.id
         ).pipe(
            map(filterDescs => slice.actions.assetFilterDescsLoaded({
               filter: action.payload.filter,
               filterDescs
            })),
            catchError(err => of(
               slice.actions.assetFilterDescsLoadingError({
                  filter: action.payload.filter,
                  err
               })))
         )
         }
      )
   )

}


const loadFilterDesc: TEpic<ActionType<typeof slice.actions.loadAssetFilterDesc>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadAssetFilterDesc.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.readAssetGroupFilterDesc(
            action.payload.filter.companyId,
            "_", //action.payload.filter.filterGroupId,
            action.payload.filter.id,
            action.payload.descId
         ).pipe(
            map(filterDesc => slice.actions.assetFilterDescLoaded({
               filter: action.payload.filter,
               filterDesc
            })),
            catchError(err => of(slice.actions.assetFlterDescLoadingError({
               f_fd_id: action.payload.filter.id + action.payload.descId,
               err
            })))
         )
      )
   )

}


const createAssetFilter: TEpic<ActionType<typeof slice.actions.createAssetFilter>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.createAssetFilter.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.createAssetGroupFilter({
            _primary_term: -1,
            _seq_no: -1,
            companyId: action.payload.companyId,
            filterGroupId: "_", //action.payload.assetGroupId,
            id: "",
            vendor: action.payload.vendor,
            product: action.payload.product
         }).pipe(
            map(filter => slice.actions.assetFilterCreated({ filter })),
            catchError(err => of(slice.actions.assetFilterCreationError(err)))
         )
      )

   )

}


const createAssetFilterDesc: TEpic<ActionType<typeof slice.actions.createAssetFilterDesc>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.createAssetFilterDesc.type
      ),
      switchMap(
         action => {
            return deps.api.companyDataProvider.createAssetGroupFilterDesc({
               _primary_term: -1,
               _seq_no: -1,
               companyId: action.payload.companyId,
               filterGroupId: "_", //action.payload.assetGroupId,
               filterId: action.payload.filterId,
               id: "",
               field: action.payload.field,
               operator: action.payload.operator,
               value: action.payload.value,
               negate: action.payload.negate
            }).pipe(
               map(assetFilterDesc => slice.actions.assetFilterDescCreated({ assetFilterDesc })),
               catchError(err => of(slice.actions.assetFilterDescCreationError({ filterId: action.payload.filterId, err})))
            )
         }
      )

   )


}


const deleteAssetFilter: TEpic<ActionType<typeof slice.actions.deleteAssetFilterById>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.deleteAssetFilterById.type
      ),
      switchMap(
         action => {
            const filter = state.value.selectedAssetFilters.filters.find(f => f.id === action.payload.filterId)
            if (!filter) return of(slice.actions.assetFilterDeletionError({ id: action.payload.filterId, err: new Error("Filter not loaded") }));

            return deps.api.companyDataProvider.deleteAssetGroupFilter(
               filter.companyId,
               "_", //filter.filterGroupId,
               filter.id
            ).pipe(
               map(() => slice.actions.assetFilterDeleted({ id: action.payload.filterId })),
               catchError(err => of(slice.actions.assetFilterDeletionError({ id: action.payload.filterId, err })))
            )
         },
      )

   )


}


const deleteAssetFilterDesc: TEpic<ActionType<typeof slice.actions.deleteAssetFilterDescById>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.deleteAssetFilterDescById.type
      ),
      switchMap(
         action => {

            if (!state.value.selectedAssetFilters.filterDescs[action.payload.filterId])
               return of(slice.actions.assetFilterDescDeletionError({
                  filterId: action.payload.filterId,
                  id: action.payload.id,
                  err: new Error("Asset group filter not loaded")
               }));

            const filterDesc = state.value.selectedAssetFilters.filterDescs[action.payload.filterId].find(fd => fd.id === action.payload.id);

            if (!filterDesc) return of (slice.actions.assetFilterDescDeletionError({
               filterId: action.payload.filterId,
               id: action.payload.id,
               err: new Error("Asset group filter desc not loaded")
            }))

            return deps.api.companyDataProvider.deleteAssetGroupFilterDesc(
               filterDesc.companyId,
               "_", // filterDesc.filterGroupId,
               filterDesc.filterId,
               filterDesc.id
            ).pipe(
               map(() => slice.actions.assetFilterDescDeleted({ filterId: action.payload.filterId, id: action.payload.id })),
               catchError(err => of(slice.actions.assetFilterDescDeletionError({ filterId: action.payload.filterId, id: action.payload.id, err })))
            )
         }
      )

   )


}


const createAssetFilterAndDesc: TEpic<ActionType<typeof slice.actions.createAssetFilterAndDesc>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.createAssetFilterAndDesc.type
      ),
      tap(
         a => { console.log(a) }
      ),
      concatMap(

         action => {

            return merge(

               of(slice.actions.createAssetFilterAndDesc_FilterStage()),

               deps.api.companyDataProvider.createAssetGroupFilter({
                  _primary_term: -1,
                  _seq_no: -1,
                  companyId: action.payload.companyId,
                  filterGroupId: "_", //action.payload.assetGroupId,
                  id: "",
                  vendor: action.payload.vendor,
                  product: action.payload.product
               }).pipe(

                  concatMap(

                     filter => [

                        slice.actions.assetFilterCreated({ filter }),

                        slice.actions.createAssetFilterDesc({
                           companyId: filter.companyId,
                           filterId: filter.id,
                           field: action.payload.field,
                           operator: action.payload.operator,
                           value: action.payload.value,
                           negate: action.payload.negate
                        })

                     ]
                  ),

                  catchError(err => of(slice.actions.assetFilterCreationError({ err })))
               )

            )

         }

      )

   )

}

const epics: any = [
   saveSelectedAssetFilters,
   saveSelectedAssetFiltersAs,
   loadFilters,
   filtersLoaded,
   loadFilter,
   filterLoaded,
   loadFilterDescs,
   loadFilterDesc,
   createAssetFilter,
   createAssetFilterDesc,
   deleteAssetFilter,
   deleteAssetFilterDesc,
   createAssetFilterAndDesc
];


export default epics;